import { Environment, OrbitControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { easeOutCubic, getResponsiveCameraZ } from "../helpers";

const CAMERA_MOVE_THRESHOLD = 0.5;
const DEFAULT_TARGET = new THREE.Vector3(0, 0, 0);

export interface CameraControlsProps {
  enabled: boolean;
  onCameraMoved?: (moved: boolean) => void;
  returnToHome?: boolean;
  onReturnComplete?: () => void;
}

export function CameraControls({
  enabled,
  onCameraMoved,
  returnToHome,
  onReturnComplete,
}: CameraControlsProps) {
  const { camera, size } = useThree();
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const isReturning = useRef(false);
  const returnStartPos = useRef(new THREE.Vector3());
  const returnStartTarget = useRef(new THREE.Vector3());
  const returnStartTime = useRef<number | null>(null);

  const defaultCameraPos = useMemo(() => {
    const z = getResponsiveCameraZ(size.width, size.height);
    return new THREE.Vector3(0, 0, z);
  }, [size.width, size.height]);

  useFrame(({ clock }) => {
    const controls = controlsRef.current;

    if (returnToHome && !isReturning.current) {
      isReturning.current = true;
      returnStartPos.current.copy(camera.position);
      // Capture current target from OrbitControls
      if (controls) {
        returnStartTarget.current.copy(controls.target);
      }
      returnStartTime.current = clock.getElapsedTime();
    }

    if (isReturning.current && returnStartTime.current !== null) {
      const elapsed = clock.getElapsedTime() - returnStartTime.current;
      const duration = 1.5;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      // Lerp camera position
      camera.position.lerpVectors(
        returnStartPos.current,
        defaultCameraPos,
        eased,
      );

      // Lerp OrbitControls target back to origin
      if (controls) {
        controls.target.lerpVectors(
          returnStartTarget.current,
          DEFAULT_TARGET,
          eased,
        );
        controls.update();
      }

      if (progress >= 1) {
        isReturning.current = false;
        returnStartTime.current = null;
        onReturnComplete?.();
      }
      return;
    }

    if (enabled && onCameraMoved) {
      const distance = camera.position.distanceTo(defaultCameraPos);
      // Also check if target has moved from origin (due to panning)
      const targetDistance = controls
        ? controls.target.distanceTo(DEFAULT_TARGET)
        : 0;
      onCameraMoved(
        distance > CAMERA_MOVE_THRESHOLD ||
          targetDistance > CAMERA_MOVE_THRESHOLD,
      );
    }
  });

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        enabled={enabled && !isReturning.current}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={0}
        maxDistance={10}
        minPolarAngle={Math.PI * 0.3}
        maxPolarAngle={Math.PI * 0.5}
        rotateSpeed={0.5}
        zoomSpeed={0.5}
      />
      {false && <Environment preset="warehouse" />}
    </>
  );
}
